import {
  Box,
  Button,
  Typography,
  Link,
  Stack,
  Paper,
  Alert,
  SvgIcon,
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonIcon from '@mui/icons-material/Person';

// Custom BigQuery Icon
const BigQueryIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 81 81">
    <g stroke="none">
      <path d="M18.919 35.318v10.519a19.64 19.64 0 0 0 6.84 6.96V35.318z" fill="currentColor" opacity="0.7"/>
      <path d="M31.558 25.639v30.118a30.48 30.48 0 0 0 4.44.24c1.332.083 2.668.083 4 0V25.639z" fill="currentColor"/>
      <path d="M46.558 39.438v13.159a20 20 0 0 0 6.8-7.28v-5.88z" fill="currentColor" opacity="0.7"/>
      <path d="M62.957 57.277l-5.68 5.68a1.68 1.68 0 0 0 0 2.4l14.159 14.159a1.68 1.68 0 0 0 2.36 0l5.72-5.72a1.68 1.68 0 0 0 0-2.36L65.357 57.277a1.68 1.68 0 0 0-2.4 0" fill="currentColor"/>
      <path d="M35.998 0C16.117 0 0 16.117 0 35.998s16.117 35.998 35.998 35.998 35.998-16.117 35.998-35.998S55.879 0 35.998 0m0 62.757C21.252 62.735 9.312 50.771 9.32 36.025S21.279 9.327 36.025 9.32s26.71 11.933 26.732 26.679a26.72 26.72 0 0 1-26.759 26.759" fill="currentColor"/>
    </g>
  </SvgIcon>
);

const BIGQUERY_URLS = {
  EXPORT_LOG: 'https://console.cloud.google.com/bigquery?project=prod-hart-business-d-3637ddb0&ws=!1m5!1m4!4m3!1sprod-hart-business-d-3637ddb0!2shartjobs_audit_logs!3sexport_logs',
  USER_ADMIN: 'https://console.cloud.google.com/bigquery?project=prod-hart-business-d-3637ddb0&ws=!1m5!1m4!4m3!1sprod-hart-business-d-3637ddb0!2scta_analysis!3shart_jobs_users', 
};
const USER_REQUEST_LINK = 'mailto:help@techallies.org?subject=Hart%20User%20Change%20Request';
const PASSWORD_RESET_LINK = 'https://cta-pad.com/forgot-password';

const UserAdministration = () => {
  const handleOpenBigQuery = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <BigQueryIcon sx={{ fontSize: 20, color: '#4FC3F7' }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            BigQuery User Datasets
          </Typography>
        </Box>
        
        <Stack spacing={2} sx={{ maxWidth: 600 }}>
          <Button
            variant="outlined"
            startIcon={<StorageIcon />}
            endIcon={<OpenInNewIcon />}
            onClick={() => handleOpenBigQuery(BIGQUERY_URLS.EXPORT_LOG)}
            fullWidth
            sx={{ 
              justifyContent: 'space-between',
              py: 1.25,
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              User Logs Dataset
            </Box>
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<StorageIcon />}
            endIcon={<OpenInNewIcon />}
            onClick={() => handleOpenBigQuery(BIGQUERY_URLS.USER_ADMIN)}
            fullWidth
            sx={{ 
              justifyContent: 'space-between',
              py: 1.25,
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              User Admin Dataset
            </Box>
          </Button>
        </Stack>
      </Box>

      <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <PersonIcon sx={{ fontSize: 20, color: '#9C27B0' }} />
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            User Management
          </Typography>
        </Box>

        {/* Primary Action Area */}
        <Box 
          sx={{ 
            mb: 3, 
            p: 3, 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" gutterBottom sx={{ fontWeight: 600, mb: 0.5 }}>
              Need changes made to a user?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submit a request for user account modifications, password resets, permission changes, and more.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            endIcon={<OpenInNewIcon />}
            onClick={() => window.open(USER_REQUEST_LINK, '_blank', 'noopener,noreferrer')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Submit User Request
          </Button>
        </Box>

        {/* Supporting Information */}
        <Box 
          sx={{ 
            p: 3, 
            bgcolor: 'grey.50',
            borderRadius: 1,
          }}
        >
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 600 }}>
            Available Changes & Required Information
          </Typography>

          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: 'text.secondary' }}>
                Create A New User
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please include the new user's first and last name, Hart email, and permission level (viewer, contributor, editor or administrator)
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: 'text.secondary' }}>
                Reset a User's Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Users can reset their own password at{' '}
                <Link
                  href={PASSWORD_RESET_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  this link
                </Link>
                .
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: 'text.secondary' }}>
                Reset a User's 2FA
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please include the user's Hart email and what type of 2FA error they are getting.
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: 'text.secondary' }}>
                Change a User's Permission Level
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please include the user's Hart email and the new permission level.
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: 'text.secondary' }}>
                Suspend User
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please include the user's Hart email. If the suspension is urgent, please flag in the email.
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom sx={{ color: 'text.secondary' }}>
                Change User Information (Name, etc.)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please include the user's Hart email and the requested changes.
              </Typography>
            </Box>

            <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary" fontStyle="italic">
                For all other requests and questions, submit a request using the button above.
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default UserAdministration;
